import React from 'react';
import { Form, Row, Col, Dropdown } from 'react-bootstrap';
import styles from '../AddProducts.module.scss';

const VoucherThemeSelect = ({
    voucherThemes,
    value,
    onChange,
    error,
    edit,
    loading,
    searchType,
    setSearchType,
    searchTerm,
    setSearchTerm
}) => {
    // In edit mode, `value` is expected to be a single theme object (or null/undefined)
    // In create mode, `value` is expected to be an array of theme id strings
    const isEdit = Boolean(edit);

    const handleSelect = (theme) => {
        if (!theme) return;

        if (isEdit) {
            // For edit (single-select) pass the full theme object to onChange
            onChange({ target: { name: 'selectedVoucherThemes', value: theme } });
        } else {
            // For create (multi-select) add the id (string) if not already present
            const idStr = String(theme.id);
            const exists = Array.isArray(value) && value.includes(idStr);
            const newVals = exists ? value : [...(value || []), idStr];
            onChange({ target: { name: 'selectedVoucherThemes', value: newVals } });
        }
    };

    const handleRemove = (themeId) => {
        if (isEdit) {
            // Clear selection
            onChange({ target: { name: 'selectedVoucherThemes', value: null } });
        } else {
            const idStr = String(themeId);
            const newVals = (value || []).filter((id) => id !== idStr);
            onChange({ target: { name: 'selectedVoucherThemes', value: newVals } });
        }
    };

    // Determine label shown in toggle
    let toggleLabel = 'Select voucher themes';
    if (isEdit) {
        if (value && value.name) toggleLabel = value.name;
    } else {
        if (Array.isArray(value) && value.length > 0) {
            toggleLabel = `${value.length} selected`;
        }
    }

    return (
        <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm={3}>
                Voucher Themes <span className='text-danger'>*</span>
            </Form.Label>
            <Col sm={9}>
                <Dropdown
                    className="w-100 mb-2"
                    onToggle={(nextShow) => {
                        // When the dropdown closes, clear the parent's search term
                        if (!nextShow && typeof setSearchTerm === 'function') {
                            setSearchTerm('');
                        }
                    }}
                >
                    <Dropdown.Toggle
                        variant='light'
                        className={`form-select text-start ${styles.noCaret} w-100 ${error ? 'is-invalid' : ''}`}
                        id='voucher-theme-select'
                        disabled={loading}
                    >
                        {toggleLabel}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className={styles.dropdownMenu}>
                        <div className={styles.searchContainer}>
                            <div className="d-flex gap-2">
                                {setSearchType && (
                                    <Form.Control
                                        size='sm'
                                        as="select"
                                        className='form-select'
                                        value={searchType || 'name'}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSearchType(val);
                                        }}
                                        style={{ width: 'auto' }}
                                    >
                                        <option value="name">Name</option>
                                    </Form.Control>
                                )}
                                {setSearchTerm && (
                                    <Form.Control
                                        size='sm'
                                        type='text'
                                        placeholder='Search voucher themes...'
                                        value={searchTerm || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSearchTerm(val);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={styles.scrollableMenu}>
                            {voucherThemes.length === 0 && !loading && (
                                <div className={styles.noMatches}>No themes found</div>
                            )}
                            {loading ? (
                                <div className={styles.noMatches}>Loading themes...</div>
                            ) : (
                                <>
                                    {!(searchTerm && String(searchTerm).trim() !== '') && (
                                        <Dropdown.Item
                                            key="none"
                                            active={!value || (isEdit ? !value : value.length === 0)}
                                            onClick={() => onChange({ target: { name: 'selectedVoucherThemes', value: isEdit ? null : [] } })}
                                        >
                                            Select voucher themes
                                        </Dropdown.Item>
                                    )}
                                    {voucherThemes.map((theme) => (
                                        <Dropdown.Item
                                            key={theme.id}
                                            active={isEdit ? (value && value.id === theme.id) : (Array.isArray(value) && value.includes(String(theme.id)))}
                                            onClick={() => handleSelect(theme)}
                                        >
                                            {theme.name}
                                        </Dropdown.Item>
                                    ))}
                                </>
                            )}
                        </div>
                    </Dropdown.Menu>
                </Dropdown>

                {/* Render chips only for create (multi-select) mode */}
                {!isEdit && (
                    <div className={styles.themeChips}>
                        {(value || []).map((themeId) => {
                            const theme = voucherThemes.find(t => String(t.id) === themeId);
                            if (!theme) return null;
                            return (
                                <span key={theme.id} className={styles.themeChip}>
                                    {theme.name}
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(theme.id)}
                                        className={styles.removeChip}
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {error && (
                    <div className="invalid-feedback">
                        {error}
                    </div>
                )}
            </Col>
        </Form.Group>
    );
};

export default VoucherThemeSelect;
